import React, { useContext, FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';

// OL
// import WKT from 'ol/format/WKT';
// import OlPolygon from 'ol/geom/Polygon';
// import { Feature as OlFeature } from 'ol';

// MUI
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Grid } from '@mui/material';

// MUI Icons
import { EditRoad } from '@mui/icons-material';

// Custom Components
import useFormController from '@/components/useFormController';
import DynamicDialog from '@/ui/Dialog/DynamicDialog';
import DialogToolbarHeading from '@/ui/Dialog/DialogToolbarHeading';
import DialogToolbarButtonClose from '@/ui/Dialog/ToolbarButtons/DialogToolbarButtonClose';
import ToolbarFillContent from '@/ui/Toolbar/ToolbarFillContent';
import DialogActionButtonClose from '@/ui/Dialog/ActionButtons/DialogActionButtonClose';
import DialogActionButtonAdd from '@/ui/Dialog/ActionButtons/DialogActionButtonAdd';
import DialogActionButtonSave from '@/ui/Dialog/ActionButtons/DialogActionButtonSave';
import DialogContext, { IDialogProps } from '@/context/DialogContext/DialogContext';
import SnackbarContext from '@/context/SnackbarContext/SnackbarContext';
import { GridContainer } from '@/ui/Grid';
import FormContent from '@/components/FormContent';
import DialogToolbarMenuItemDeactivate from '@/ui/Dialog/ToolbarMenuItems/DialogToolbarMenuItemDeactivate';
import DialogToolbarMenuItemActivate from '@/ui/Dialog/ToolbarMenuItems/DialogToolbarMenuItemActivate';
import DialogToolbarMenuButton from '@/ui/Dialog/DialogToolbarMenuButton';
import DialogToolbarMenuItem from '@/ui/Dialog/DialogToolbarMenuItem';
import UserContext from '@/context/UserContext/UserContext';
import Permissions from '@/lib/permissions';
import DialogToolbarMenuItemDelete from '@/ui/Dialog/ToolbarMenuItems/DialogToolbarMenuItemDelete';
import DataController from '@/lib/DataController';
import DialogInfoDeactivated from '@/ui/Dialog/DialogInfoDeactivated';
import DialogInfoMeta from '@/ui/Dialog/DialogInfoMeta';

// Types
import { ClosingDetails, FormControllerProps } from '@/@types/components/formController';


const title = 'koncesije:titles.koncesija';

// const wktFormat = new WKT();

const KoncesijeDialog: FunctionComponent<IDialogProps> = (props) => {
  const { hideDialog } = useContext(DialogContext);
  const { showNotification } = useContext(SnackbarContext);
  const userContext = useContext(UserContext);
  const { t } = useTranslation();

  const [allowGeometrychange, setAllowGeometryChange] = useState(false);

  const { dc, mode, form, onClose, level, columns = 2 } = props;

  const geometryChangeType = 'Polygon';
  const mapId = 65;

  const canDelete = userContext?.hasAnyPermission([Permissions.KoncesijeEdit]);

  const {
    record,
    validation,
    fields,
    pickerDataControllers,
    onFieldChange,
    doInsert,
    doUpdate,
    doClose,
    doDelete,
    doRefresh,
    doActivate,
    doDeactivate,
  } = useFormController(props as FormControllerProps);

  const handleFieldChange = (value: any, source: string) => {
    // WKT to Feature // Lets find a better solution shall we?
    /* if (source === 'wkt') {
      try {
        if (!value || typeof value !== 'string') return;

        // Parse WKT (EPSG:3765)
        const geom3765 = wktFormat.readGeometry(value) as OlPolygon;

        // Transform to map projection (EPSG:3857)
        const geom3857 = geom3765.transform('EPSG:3765', 'EPSG:3857');

        const feature = new OlFeature({
          geometry: geom3857,
        });
        // console.log(geom3765.getArea(), 'povrsina')
        // Return to form controller
        onFieldChange(feature, 'geom');
        onFieldChange(value, 'wkt');
        onFieldChange(Math.round(geom3765.getArea() * 100) / 100, 'povrsina');
        onFieldChange(true, 'is_geom_changed');
      } catch (err) {
        // will happen often
        console.warn('Invalid WKT:', err);
        onFieldChange(value, 'wkt');
        onFieldChange(null, 'geom');
      }
      return;
    }
    // Feature to WKT
    if (source === 'geom') {
      try {
        if (!value || !(value instanceof OlFeature)) return;

        const geom3857 = value.getGeometry() as OlPolygon;

        if (!geom3857) return;

        // Clone so we don't mutate OL geometry
        const clone3765 = geom3857.clone().transform('EPSG:3857', 'EPSG:3765');

        const wkt = wktFormat.writeGeometry(clone3765);
        // console.log(clone3765.getArea(), 'povrsina')

        // Return WKT to record
        onFieldChange(wkt, 'wkt');
        onFieldChange(value, 'geom');
        onFieldChange(Math.round(clone3765.getArea() * 100) / 100, 'povrsina');
      } catch (err) {
        // shouldnt happen?
        console.warn('Failed to convert feature → WKT:', err);
        onFieldChange(value, 'geom');
      }
      return;
    } */
    // Default
    if (onFieldChange) {
      onFieldChange(value, source);
    }
  };

  const handleInsert = () => {
    if (doInsert) {
      doInsert()
        .then((result) => {
          if (result.success) {
            const msg = 'messages.createRecord';
            showNotification(msg, 'success');
            close({ dataChanged: true, action: 'insert' });
          } else {
            const msg = `error.addRecord`;
            showNotification(msg, 'error');
          }
        })
        .catch((resp) => {
          showNotification(resp.error, 'error');
        });
    }
  };

  const handleUpdate = () => {
    if (doUpdate) {
      doUpdate()
        .then((result) => {
          if (result.success) {
            const msg = 'messages.updateRecord';
            showNotification(msg, 'success');
            close({ dataChanged: true, action: 'update' });
          } else {
            const msg = `error.editRecord`;
            showNotification(msg, 'error');
          }
        })
        .catch((resp) => {
          showNotification(resp.error, 'error');
        });
    }
  };

  const handleClose = () => {
    if (doClose) {
      doClose()
        .then((resolve) => {
          if (resolve.success) {
            // close
            close({ dataChanged: false, action: 'cancel' });
          }
        })
        .catch((e) => console.error(e));
    }
  };

  const handleActivate = () => {
    if (doActivate) {
      doActivate()
        .then((result) => {
          if (result.success) {
            const msg = 'messages.activateRecord';
            showNotification(msg, 'success');
            doRefresh();
          } else {
            const msg = 'error.activateRecord';
            showNotification(msg, 'error');
          }
        })
        .catch((resp) => {
          showNotification(resp.error, 'error');
        });
    }
  };

  const handleDeactivate = () => {
    if (doDeactivate) {
      doDeactivate()
        .then((result) => {
          if (result.success) {
            const msg = 'messages.deactivateRecord';
            showNotification(msg, 'success');
            close({ dataChanged: true, action: 'update' });
          } else {
            const msg = 'error.deactivateRecord';
            showNotification(msg, 'error');
          }
        })
        .catch((resp) => {
          showNotification(resp.error, 'error');
        });
    }
  };

  const handleAllowGeomChange = (state: boolean) => {
    setAllowGeometryChange(state);
  };
  const close = (result: ClosingDetails) => {
    onClose(result);
    hideDialog(level);
  };

  const handleDelete = () => {
    if (doDelete) {
      doDelete().then((result) => {
        if (result.success) {
          const msg = 'messages.deleteRecord';
          showNotification(msg, 'success');
          close({ dataChanged: true, action: 'update' });
        } else {
          const msg = 'error.deleteRecord';
          showNotification(msg, 'error');
        }
      });
    }
  };

  let cmode = mode;
  if (record.active === false) {
    cmode = 'view';
  }

  const mapFields = (dc as DataController).getFormFields(allowGeometrychange ? 'map' : 'mapNoEdit');

  return (
    <DynamicDialog open maxWidth="xl" onClose={handleClose}>
      <DialogTitle>
        <Toolbar variant="dense" disableGutters>
          <DialogToolbarHeading>{t(title)}</DialogToolbarHeading>
          <ToolbarFillContent />
          {/* Toolbar menu visible only when user is not in pure view mode */}
          {mode !== 'view' && (
            <DialogToolbarMenuButton>
              {/* Geometry change toggle (only in insert or update AND record is active) */}
              {(mode === 'insert' || (mode === 'update' && record.active)) && (
                <DialogToolbarMenuItem
                  onClick={() => handleAllowGeomChange(!allowGeometrychange)}
                  label={allowGeometrychange ? t('buttons.geom_change_off') : t('buttons.geom_change_on')}
                  icon={<EditRoad />}
                />
              )}

              {/* Delete only on update and when user has permission */}
              {mode === 'update' && canDelete && <DialogToolbarMenuItemDelete onClick={handleDelete} />}

              {/* Activate / Deactivate visible as long as we are in UPDATE mode */}
              {mode === 'update' &&
                (record.active ? (
                  <DialogToolbarMenuItemDeactivate onClick={handleDeactivate} />
                ) : (
                  <DialogToolbarMenuItemActivate onClick={handleActivate} />
                ))}
            </DialogToolbarMenuButton>
          )}

          <DialogToolbarButtonClose onClick={handleClose} />
        </Toolbar>
      </DialogTitle>
      <DialogContent>
        <GridContainer my={2}>
          <Grid container size={{ xs: 12, md: 6 }}>
            <FormContent
              fieldNames={dc.getFormFieldsNames('map')}
              record={record}
              validation={validation}
              fields={mapFields}
              onFieldChange={handleFieldChange}
              mode={cmode}
              columns={columns}
              pickerDataControllers={pickerDataControllers}
              geometryChangeType={allowGeometrychange ? geometryChangeType : undefined}
              mapId={mapId}
              mapHeight="88.5%"
            />
          </Grid>
          <Grid container size={{ xs: 12, md: 6 }}>
            <FormContent
              fieldNames={dc.getFormFieldsNames(form || mode)}
              record={record}
              validation={validation}
              fields={fields}
              onFieldChange={handleFieldChange}
              layout={dc.getForm(form || mode)?.layout}
              mode={cmode}
              columns={columns}
              pickerDataControllers={pickerDataControllers}
            />
          </Grid>
        </GridContainer>
      </DialogContent>
      <DialogActions>
        <Box sx={{ flexGrow: 1 }}>
          {mode === 'update' && !record.active ? (
            <DialogInfoDeactivated />
          ) : (
            <DialogInfoMeta record={record} />
          )}
        </Box>
        <DialogActionButtonClose variant="outlined" onClick={handleClose} />
        {mode === 'insert' ? (
          <DialogActionButtonAdd variant="contained" onClick={handleInsert} />
        ) : mode === 'update' && record.active === true ? (
          <DialogActionButtonSave variant="contained" onClick={handleUpdate} />
        ) : null}
      </DialogActions>
    </DynamicDialog>
  );
};

export default KoncesijeDialog;